import { useState } from 'react';
import { comments as seedComments } from '@/mocks/home';
import SectionHeading from './SectionHeading';

interface LocalComment {
  name: string;
  avatar: string;
  time: string;
  text: string;
  likes: number;
  replies: number;
}

export default function Discussion() {
  const [likes, setLikes] = useState<Record<number, boolean>>(() =>
    seedComments.reduce<Record<number, boolean>>((acc, c, i) => {
      acc[i] = c.liked;
      return acc;
    }, {}),
  );
  const [draft, setDraft] = useState('');
  const [myComments, setMyComments] = useState<LocalComment[]>([]);
  const [tip, setTip] = useState('');

  const toggleLike = (index: number) => {
    setLikes((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleAdd = () => {
    const value = draft.trim();
    if (!value) {
      setTip('先写下你的辨别经验，再发布。');
      return;
    }
    setMyComments((prev) => [
      {
        name: '我',
        avatar:
          'https://readdy.ai/api/search-image?query=warm%20editorial%20portrait%20of%20a%20friendly%20reader%2C%20soft%20studio%20light%2C%20muted%20cream%20background%2C%20clean%20magazine%20photography%2C%20high%20detail&width=120&height=120&seq=comment-avatar-me&orientation=squarish',
        time: '刚刚',
        text: value,
        likes: 0,
        replies: 0,
      },
      ...prev,
    ]);
    setDraft('');
    setTip('已发布到本地讨论区（演示展示，不会真实提交）。');
  };

  return (
    <section id="discuss" className="w-full border-y border-background-200 bg-background-100">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-[0.72fr_1.28fr] lg:gap-14">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <SectionHeading
            index="05"
            kicker="读者讨论"
            title="每天都有读者在这里交换辨别经验"
            desc="有人分享了差点中招的经历，有人总结了核对的小技巧。辨别真假这件事，多一个人提醒，就少一个人踩坑。"
          />
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-2xl font-black text-primary-600">4,812</p>
              <p className="mt-1 text-xs text-foreground-600">条辨别经验</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-2xl font-black text-accent-600">1,206</p>
              <p className="mt-1 text-xs text-foreground-600">位活跃读者</p>
            </div>
          </div>
        </div>

        <div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-edit-2-line" />
              </span>
              <p className="font-heading text-base font-bold text-foreground-950">写下你的辨别经验</p>
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={3}
              placeholder="例如：我发现那个地址的域名里多了个连字符，核对官方入口后立刻退出了……"
              className="mt-4 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs text-foreground-500">{draft.length} / 500 字</span>
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-fill" />
                发布评论
              </button>
            </div>
            {tip && <p className="mt-3 text-xs text-foreground-600">{tip}</p>}
          </div>

          <ul className="mt-6 flex flex-col gap-4">
            {[...myComments, ...seedComments].map((comment, index) => {
              const isLiked = !!likes[index];
              return (
                <li
                  key={`${comment.name}-${index}`}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-start gap-4">
                    <span className="h-11 w-11 flex-none overflow-hidden rounded-full border border-background-200">
                      <img
                        src={comment.avatar}
                        alt={`${comment.name} 的头像`}
                        title={`${comment.name} 的读者头像`}
                        className="h-full w-full object-top"
                      />
                    </span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-heading text-sm font-bold text-foreground-950">{comment.name}</span>
                        <span className="text-xs text-foreground-500">{comment.time}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.text}</p>
                      <div className="mt-3 flex items-center gap-5">
                        <button
                          type="button"
                          onClick={() => toggleLike(index)}
                          className={`inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold transition-colors ${
                            isLiked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                          }`}
                        >
                          <i className={isLiked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                          {comment.likes + (isLiked ? 1 : 0)}
                        </button>
                        <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                          <i className="ri-chat-3-line" />
                          {comment.replies} 条回复
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                          <i className="ri-share-forward-line" />
                          分享
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}